import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import { formatDate } from '../../utils/dateFormatter'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'
import { BlogPost } from '../../types/blog'

const PlusIcon = () => (
  <svg className='tw-w-4 tw-h-4' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path d='M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z' />
  </svg>
)

const ExclamationCircleIcon = () => (
  <svg className='tw-w-5 tw-h-5 tw-mr-2 tw-text-red-600' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z' clipRule='evenodd' />
  </svg>
)

const NewspaperIcon = () => (
  <svg className='tw-w-12 tw-h-12 tw-mb-3 tw-text-slate-400' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18V7.875c0-.621.504-1.125 1.125-1.125H6.75M12 7.5V9m0 3V9m0 3v2.25m0 3v-2.25m0 0l1.5-1.5m-1.5 1.5l-1.5-1.5m3-3l1.5-1.5m-1.5 1.5l-1.5-1.5' />
  </svg>
)

const ArrowLeftIcon = () => (
  <svg className='tw-w-4 tw-h-4' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z' clipRule='evenodd' />
  </svg>
)

const ArrowRightIcon = () => (
  <svg className='tw-w-4 tw-h-4' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z' clipRule='evenodd' />
  </svg>
)

const BlogPostCard = ({ post }: { post: BlogPost }) => (
  <div className='tw-max-w-xs tw-rounded-sm tw-overflow-hidden tw-h-full tw-flex tw-flex-col tw-group hover:tw-shadow-2xl tw-transition-shadow tw-duration-300 tw-ease-in-out'>
    <div className='tw-overflow-hidden'>
      <img
        src={post.imagePresignedUrl || post.imageUrl || `/api/uploads/image/${post.image}`}
        alt={post.title}
        className='tw-w-full tw-h-64 tw-object-cover group-hover:tw-scale-110 tw-transition-transform tw-duration-300 tw-ease-in-out'
      />
    </div>
    <div className='tw-p-4 md:tw-p-6 xl:tw-p-8 tw-flex tw-flex-col tw-flex-grow'>
      {(post.author != null) && <span className='tw-text-xs tw-text-green'>
        {post.author.name}
                                </span>}
      <h5 className='tw-mt-1 tw-text-base tw-font-semibold tw-tracking-wide tw-text-teal tw-line-clamp-2'>{post.title}</h5>

      <div className='tw-mt-4 tw-flex-grow'>
        <p className='tw-text-sm tw-line-clamp-3'>
          {post.description
            ? (post.description.length > 80
                ? `${post.description.substring(0, 80)}...`
                : post.description)
            : 'Brak opisu'}
        </p>
      </div>
      <Link
        to={`/blog/${post.slug}`}
        className='tw-mt-auto tw-inline-flex tw-items-center tw-text-sm tw-font-semibold tw-text-green group-hover:tw-underline'
      >
        Czytaj więcej
        <ArrowRightIcon />
      </Link>
    </div>
  </div>
)

const BlogSection = ({ addPageBanner = true }: { addPageBanner?: boolean }): JSX.Element => {
  const [blogPostsData, setBlogPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)

  const requests = useRequests()
  const { username, isSuperadmin } = useSelector((state: State) => state.user)

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await requests.getBlogPosts({ page: currentPage, limit: 6 })

        const formattedPosts = response.data.data.map((post: any) => ({
          ...post,
          date: formatDate(post.date)
        }))

        setBlogPosts(formattedPosts)
        setTotalPages(response.data.totalPages)
      } catch (err) {
        setError('Nie udało się załadować artykułów. Spróbuj ponownie później.')
        console.error('Błąd podczas pobierania artykułów:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchBlogPosts()
  }, [currentPage, requests])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      window.scrollTo(0, 0)
    }
  }

  const PaginationLink: React.FC<{
    page?: number
    onClick: () => void
    isActive?: boolean
    isDisabled?: boolean
    children: React.ReactNode
  }> = ({ onClick, isActive, isDisabled, children }) => (
    <li>
      <button
        type='button'
        onClick={onClick}
        disabled={isDisabled}
        aria-current={isActive ? 'page' : undefined}
        className={`
            tw-flex tw-items-center tw-justify-center tw-px-3 tw-h-8 tw-leading-tight 
            tw-border tw-transition-colors tw-duration-150 tw-rounded-md
            ${isActive
                    ? 'tw-bg-green tw-text-white tw-border-green hover:tw-opacity-90'
                    : 'tw-text-slate-600 tw-bg-white tw-border-slate-300 hover:tw-bg-slate-100 hover:tw-text-slate-700'
                }
            ${isDisabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer'}
            `}
      >
        {children}
      </button>
    </li>
  )

  return (
    <section className='tw-py-20 md:tw-py-28 xl:tw-py-32 tw-pb-0 tw-container tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8'>
      <div className='tw-text-center tw-mb-10 lg:tw-mb-12'>
        <h4 className='tw-text-base md:tw-text-lg xl:tw-text-xl tw-font-semibold tw-text-teal tw-text-center'>Najnowsze artykuły</h4>
      </div>

      {username && isSuperadmin && (
        <div className='tw-flex tw-justify-end tw-mb-8'>
          <Link
            to='/blog/add'
            className='tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-green hover:tw-opacity-90 tw-text-white tw-text-sm tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors'
          >
            <PlusIcon />
            <span className='tw-ml-2'>Dodaj nowy artykuł</span>
          </Link>
        </div>
      )}

      {loading && (
        <div className='tw-text-center tw-py-20'>
          <svg className='tw-animate-spin tw-mx-auto tw-h-10 tw-w-10 tw-text-green' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
            <circle className='tw-opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
            <path className='tw-opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
          </svg>
          <p className='tw-mt-3 tw-text-slate-500'>Wczytywanie artykułów...</p>
        </div>
      )}

      {error && !loading && (
        <div className='tw-bg-red-100 tw-border-l-4 tw-border-red-500 tw-text-red-700 tw-p-4 tw-my-10 tw-rounded-md' role='alert'>
          <div className='tw-flex'>
            <div className='tw-py-1 tw-mr-3'>
              <ExclamationCircleIcon />
            </div>
            <div>
              <p className='tw-font-bold'>Błąd</p>
              <p className='tw-text-sm'>{error}</p>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && blogPostsData.length === 0 && (
        <div className='tw-text-center tw-py-20'>
          <NewspaperIcon />
          <p className='tw-text-xl tw-font-semibold tw-text-slate-700 tw-mt-2'>Brak artykułów do wyświetlenia.</p>
          <p className='tw-text-slate-500 tw-mt-1'>Sprawdź ponownie później.</p>
        </div>
      )}

      {!loading && !error && blogPostsData.length > 0 && (
        <>
          <div className='tw-mt-10 lg:tw-mt-16 tw-rounded-md'>
            <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6 lg:tw-gap-8'>
              {blogPostsData.map((post: BlogPost) => (
                <BlogPostCard post={post} key={post.id} />
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className='tw-flex tw-justify-center tw-mt-12 md:tw-mt-16'>
              <nav aria-label='Paginacja'>
                <ul className='tw-flex tw-items-center tw-space-x-2'>
                  <PaginationLink
                    onClick={() => handlePageChange(currentPage - 1)}
                    isDisabled={currentPage === 1}
                  >
                    <span className='tw-sr-only'>Poprzednia</span>
                    <ArrowLeftIcon />
                  </PaginationLink>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <PaginationLink
                        key={page}
                        onClick={() => handlePageChange(page)}
                        isActive={currentPage === page}
                      >
                        {page}
                      </PaginationLink>
                  ))}

                  <PaginationLink
                    onClick={() => handlePageChange(currentPage + 1)}
                    isDisabled={currentPage === totalPages}
                  >
                    <span className='tw-sr-only'>Następna</span>
                    <ArrowRightIcon />
                  </PaginationLink>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default BlogSection
